// Casos: estados, listado/detalle, alta, duplicados y pase a compañero.

import type { TipoCategoria } from "./catalogos";
import { api } from "./http";

export const ESTADOS_CASO = [
  "Abierto",
  "En revision",
  "Listo para enviar",
  "Enviado al proveedor",
  "Esperando proveedor",
  "Pendiente cliente",
  "Cerrado",
] as const;
export type EstadoCaso = (typeof ESTADOS_CASO)[number];

export type ImeiOut = {
  id: number;
  imei: string;
  modelo_real: string | null;
  estado_manual: string | null;
  // Semáforo calculado por el backend (manual gana; sin manual envejece).
  estado_efectivo: string | null;
  dias_sin_movimiento: number | null;
  ultima_revision: string | null;
  ficha_referencia: string | null;
  // Tipo de solución por equipo (cuando el caso se cerró por equipo).
  tipo_solucion_nombre: string | null;
  created_at: string;
};

export type CasoListItem = {
  id: number;
  folio: string;
  cliente_id: number;
  cliente_nombre: string;
  ticket_externo: string | null;
  estado: EstadoCaso;
  marca: string | null;
  modelo: string | null;
  /** De qué se trata, en un renglón. Lo que distingue dos casos del mismo cliente. */
  descripcion: string | null;
  asignado_a_id: number | null;
  asignado_a_nombre: string | null;
  imeis_count: number;
  imeis: ImeiOut[];
  notas_count: number;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  // Semáforo de inactividad (solo abiertos; null en cerrados)
  dias_sin_actividad: number | null;
  nivel_actividad: string | null;
  // Cuántas menciones pendientes tiene el usuario actual en este caso
  // (>0 = "te esperan"). El backend lo calcula por usuario.
  menciones_pendientes: number;
  /** De esas, cuántas no has abierto: 0 = la ficha va en gris. */
  menciones_sin_leer: number;
  // Posesión derivada del estado: nosotros|cliente|proveedor|cerrado.
  posesion: string | null;
  // ≥1 IMEI marcado CRIT (badge rojo en el listado).
  tiene_imei_critico: boolean;
  /** Cuándo contestó el proveedor sin que le hayamos respondido. null = nada. */
  proveedor_respondio_at?: string | null;
  jimi_work_order_no: string | null;
  /** Id del ticket en el Zendesk de Queclink (se liga solo por el TE). */
  queclink_ticket_id: number | null;
  jimi_origen: string | null;
  /** Estado del ticket en el portal del proveedor, ya en texto («En atención»,
   *  «Concluido por Jimi», «Esperan nuestra respuesta»…). null = sin dato aún. */
  jimi_ticket_estado?: string | null;
  queclink_ticket_estado?: string | null;
  /** Queclink CERRÓ el ticket: ya no acepta respuestas, va un seguimiento. */
  queclink_ticket_cerrado?: boolean;
};

export type PosesionResumen = {
  nosotros: number;
  cliente: number;
  proveedor: number;
  mios: number;
};

// Un caso puede tener multiples contactos con rol. El principal va en
// To al enviar correo cliente y los seguimientos en CC por default.
export type RolCasoContacto = "principal" | "seguimiento";

export type CasoContacto = {
  id: number;          // id de la pivote casos_contactos
  caso_id: number;
  contacto_id: number;
  nombre: string;
  email: string | null;
  telefono: string | null;
  rol: RolCasoContacto;
  activo: boolean;     // del Contacto subyacente
  added_at: string;
  added_by_id: number | null;
  added_by_nombre: string | null;
};

export type CasoModeloItem = {
  id?: number;
  marca: string | null;
  modelo: string;
  marca_id?: number | null;
  modelo_id?: number | null;
  orden?: number;
};

export type CasoDetalle = CasoListItem & {
  descripcion_inicial: string | null;
  // Tipo de solución con que se cerró el caso (a nivel caso, casos sin IMEIs
  // o "una sola para todos"). La solución por equipo vive en cada ImeiOut.
  tipo_solucion_id: number | null;
  tipo_solucion_nombre: string | null;
  // Contacto PRINCIPAL del caso (derivado de la pivote por el backend) +
  // campos del contacto para no romper UI existente (EnviarCorreoModal,
  // TabNotas que ya leen estos).
  contacto_id: number | null;
  contacto_nombre: string | null;
  contacto_email: string | null;
  contacto_telefono: string | null;
  // Todos los contactos del caso con rol — principal primero, despues
  // seguimientos.
  contactos: CasoContacto[];
  abierto_por_id: number | null;
  tipo_categoria: TipoCategoria | null;
  tipo_detalle: string | null;
  fecha_reporte_cliente: string | null;
  fecha_formulario_cliente: string | null;
  fecha_recepcion_oficina: string | null;
  fecha_envio_proveedor: string | null;
  fecha_retorno_proveedor: string | null;
  fecha_devolucion_cliente: string | null;
  fecha_reemplazo_garantia: string | null;
  fue_garantia: boolean;
  caso_padre_id: number | null;
  // Caso original del que este es duplicado (null si no es duplicado).
  duplicado_de_id: number | null;
  duplicado_de_folio: string | null;
  // Si esta seteado, el caso se cerrara automaticamente cuando el
  // worker envie ese correo (modo "enviar y cerrar" programado).
  cerrar_al_enviar_correo_id: number | null;
  // Asesor comercial heredado del cliente. null si el cliente no tiene asesor.
  asesor_comercial: { id: number; nombre: string; email: string | null; telefono: string | null } | null;
  imeis: ImeiOut[];
  modelos: CasoModeloItem[];
};

export type CasoCreate = {
  cliente_id: number;
  ticket_externo?: string | null;
  marca?: string | null;
  modelo?: string | null;
  descripcion_inicial?: string | null;
  estado: EstadoCaso;
  // Contacto principal inicial del caso. Opcional: el caso puede crearse
  // sin contacto y asignarse despues. Si viene, debe pertenecer al mismo
  // cliente_id.
  contacto_id?: number | null;
  // Seguimientos opcionales que se agregan al crear. Tambien se pueden
  // agregar despues uno por uno desde el panel de contactos del caso.
  seguimientos_ids?: number[];
  asignado_a_id?: number | null;
  tipo_categoria?: TipoCategoria | null;
  tipo_detalle?: string | null;
  fecha_reporte_cliente?: string | null;
  caso_padre_id?: number | null;
  modelos?: { marca: string; modelo: string }[];
  imeis: { imei: string; modelo_real?: string | null }[];
};

// Caso candidato a duplicado: comparte al menos un IMEI con el consultado.
// Usado por el aviso en vivo + modal de confirmacion en Nuevo Caso.
export type CasoDuplicado = {
  id: number;
  folio: string;
  estado: string;
  cerrado: boolean;
  dias_desde_cierre: number | null;
  imeis_coincidentes: string[];
  asignado_nombre: string | null;
};

export function casosPorImei(imeis: string[], excluirCasoId?: number) {
  const qs = new URLSearchParams({ imeis: imeis.join(",") });
  if (excluirCasoId != null) qs.set("excluir_caso_id", String(excluirCasoId));
  return api.get<CasoDuplicado[]>(`/api/casos/por-imei?${qs.toString()}`);
}

export function buscarCasoParaDuplicado(q: string, excluirCasoId?: number) {
  const qs = new URLSearchParams({ q });
  if (excluirCasoId != null) qs.set("excluir_caso_id", String(excluirCasoId));
  return api.get<CasoDuplicado[]>(`/api/casos/buscar-para-duplicado?${qs.toString()}`);
}

export function marcarDuplicado(casoId: number, originalCasoId: number) {
  return api.post<CasoDetalle>(`/api/casos/${casoId}/marcar-duplicado`, { original_caso_id: originalCasoId });
}

export function quitarDuplicado(casoId: number) {
  return api.post<CasoDetalle>(`/api/casos/${casoId}/quitar-duplicado`, {});
}

export type PaseCompaneroIn = {
  destinatario_id: number;
  mensaje?: string;
  reasignar?: boolean;
};

export type PaseCompaneroOut = {
  ok: boolean;
  correo_enviado: boolean;
  reasignado: boolean;
  nota_id: number | null;
};

// Compat con codigo antiguo que usa `Caso`
export type Caso = CasoListItem;
